import { clamp } from '@/core/rng';
import { SCAR_CELL } from '@/world/overlay';
import { CHUNK_SIZE } from '@/world/chunk';
import type { LandWriter } from './band';

/**
 * The band's wear on the land (spec Task 5, §17–§20), in two ledgers.
 *
 * The EnvField ledger is the *world's*: each verb is a soft disc of lost
 * biomass stamped through the same field the divine verbs write and the fire
 * reads for fuel — a camp held for a season visibly browns the ground and,
 * later, burns harder. Nobody gets a private ecology; that is the point.
 *
 * The MortalPressure ledger is the *historian's* (spec §20): a sparse store
 * on the same 32 m disturbance lattice recording what kind of use the ground
 * saw — harvest, trampling, woodcutting, visitation — decaying on a ten-day
 * half-life. Pressure is bookkeeping for belief and archaeology to read; the
 * EnvField is what the fire and the colours read. Both are written together.
 */

/** One lattice cell's accumulated mortal use, each channel 0..1. */
export interface MortalPressureCell {
  gx: number;
  gz: number;
  harvest: number;
  trampling: number;
  woodcutting: number;
  visitation: number;
  /** World-seconds of the last write — values decay from here. */
  updatedAt: number;
}

type PressureChannel = 'harvest' | 'trampling' | 'woodcutting' | 'visitation';
const CHANNELS: readonly PressureChannel[] = ['harvest', 'trampling', 'woodcutting', 'visitation'];

/** Pressure fades on a ten-day half-life: the land forgets slowly, not never. */
const PRESSURE_HALF = 10 * 86400;
/** Below this a channel is noise; a cell all-noise is forgotten. */
const NEGLIGIBLE = 0.01;

const decay = (v: number, dt: number) => v * Math.pow(0.5, dt / PRESSURE_HALF);

/** Lattice cells across one chunk — the same ratio `Overlay` uses. */
const CELLS_PER_CHUNK = CHUNK_SIZE / SCAR_CELL;

/**
 * The sparse mortal-pressure store. Pure state arithmetic on passed-in world
 * time: decay is applied lazily at read and write, so a cell untouched for a
 * year costs nothing until someone asks about it.
 */
export class MortalPressure {
  private readonly cells = new Map<string, MortalPressureCell>();

  /**
   * Add use to a soft disc of cells around (wx, wz). Existing pressure decays
   * to `at` first, then accumulates, saturating at 1 — ground can only be so
   * trampled.
   */
  stamp(
    wx: number,
    wz: number,
    radius: number,
    d: Partial<Record<PressureChannel, number>>,
    at: number,
  ): void {
    const g0x = Math.floor((wx - radius) / SCAR_CELL), g1x = Math.floor((wx + radius) / SCAR_CELL);
    const g0z = Math.floor((wz - radius) / SCAR_CELL), g1z = Math.floor((wz + radius) / SCAR_CELL);
    for (let gz = g0z; gz <= g1z; gz++) for (let gx = g0x; gx <= g1x; gx++) {
      const cx = (gx + 0.5) * SCAR_CELL, cz = (gz + 0.5) * SCAR_CELL;
      const fall = 1 - Math.hypot(cx - wx, cz - wz) / radius;
      if (fall <= 0) continue;
      const key = `${gx},${gz}`;
      const prev = this.cells.get(key);
      const dt = prev ? at - prev.updatedAt : 0;
      const cell: MortalPressureCell = prev
        ? {
            gx, gz,
            harvest: decay(prev.harvest, dt),
            trampling: decay(prev.trampling, dt),
            woodcutting: decay(prev.woodcutting, dt),
            visitation: decay(prev.visitation, dt),
            updatedAt: at,
          }
        : { gx, gz, harvest: 0, trampling: 0, woodcutting: 0, visitation: 0, updatedAt: at };
      for (const ch of CHANNELS) {
        const add = d[ch];
        if (add) cell[ch] = clamp(cell[ch] + add * fall, 0, 1);
      }
      this.cells.set(key, cell);
    }
  }

  /** The pressure under a world position, decayed to now. Null where negligible. */
  at(wx: number, wz: number, now: number): MortalPressureCell | null {
    const c = this.cells.get(
      `${Math.floor(wx / SCAR_CELL)},${Math.floor(wz / SCAR_CELL)}`);
    if (!c) return null;
    const dt = now - c.updatedAt;
    const out: MortalPressureCell = {
      gx: c.gx, gz: c.gz,
      harvest: decay(c.harvest, dt),
      trampling: decay(c.trampling, dt),
      woodcutting: decay(c.woodcutting, dt),
      visitation: decay(c.visitation, dt),
      updatedAt: now,
    };
    if (CHANNELS.every((ch) => out[ch] < NEGLIGIBLE)) return null;
    return out;
  }

  /** Forget cells whose every channel has decayed to nothing. */
  sweep(now: number): void {
    for (const [key, c] of this.cells) {
      const dt = now - c.updatedAt;
      if (CHANNELS.every((ch) => decay(c[ch], dt) < NEGLIGIBLE)) this.cells.delete(key);
    }
  }

  get size(): number {
    return this.cells.size;
  }

  /**
   * Every chunk this ledger tints, for the one caller that cannot wait for a
   * band to move: **adopting a world**.
   *
   * Wear is the sixth history layer the chunk transform stacks (`applyWear`),
   * and it was the one layer adoption had no way to invalidate — the argument
   * written into `adoptWorld` was that adopting moves every band and dirties
   * their chunks anyway, and that argument is false in two places at once.
   * Nothing moves during an adoption, and `BandSystem.restore` **empties the
   * dirty set** the argument depends on. So a loaded world kept the previous
   * one's beaten camp floors until something unrelated happened to sweep them.
   *
   * The 3×3 reach is `Overlay.scarChunks`'s, for its reason rather than by
   * imitation: `wornAt` samples the lattice, so a cell on a chunk border
   * reaches one cell into the chunk next door. (Overlay's own note says wear
   * has "the same latent hole … and has never been bitten, because a camp's
   * pressure cells are interior by construction". True of a camp. Not true of
   * the seam between two whole worlds, where every cell is in play.)
   */
  chunks(): string[] {
    const keys = new Set<string>();
    for (const c of this.cells.values()) {
      for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
        keys.add(`${Math.floor((c.gx + dx) / CELLS_PER_CHUNK)},` +
                 `${Math.floor((c.gz + dz) / CELLS_PER_CHUNK)}`);
      }
    }
    return [...keys];
  }
}

/** What one band did with one day, reduced to what the ground felt. */
export interface DayImpact {
  foraged: boolean;
  gatheredWood: boolean;
  /** A day spent in one place is a camp, whatever the activity was. */
  stationary: boolean;
  /** The band covered real ground today — feet wear a line, not a disc. */
  travelled: boolean;
}

// person-scale wear on the EnvField: foraging thins a wide ring lightly,
// wood-cutting bites hard in a small one, camping is faint — only repetition
// makes any of it visible
const FORAGE = { radius: 120, biomass: -0.028, harvest: 0.2 } as const;
const WOOD = { radius: 60, biomass: -0.07, woodcutting: 0.25 } as const;
const CAMP = { radius: 40, biomass: -0.014, trampling: 0.15, visitation: 0.1 } as const;
const TRAVEL = { radius: 48, trampling: 0.05, visitation: 0.08 } as const;

/**
 * Stamp the day's impact at the band's position — the EnvField wear the world
 * sees, and the pressure record the historians keep. Returns the chunk keys
 * ("cx,cz") whose appearance changed, for the caller's dirty set.
 */
export function stampDailyImpact(
  land: LandWriter,
  pressure: MortalPressure,
  x: number,
  z: number,
  impact: DayImpact,
  at: number,
): string[] {
  const dirty = new Set<string>();
  if (impact.foraged) {
    for (const k of land.stamp(x, z, FORAGE.radius, { biomass: FORAGE.biomass }, at)) dirty.add(k);
    pressure.stamp(x, z, FORAGE.radius, { harvest: FORAGE.harvest }, at);
  }
  if (impact.gatheredWood) {
    for (const k of land.stamp(x, z, WOOD.radius, { biomass: WOOD.biomass }, at)) dirty.add(k);
    pressure.stamp(x, z, WOOD.radius, { woodcutting: WOOD.woodcutting }, at);
  }
  if (impact.stationary) {
    for (const k of land.stamp(x, z, CAMP.radius, { biomass: CAMP.biomass }, at)) dirty.add(k);
    pressure.stamp(x, z, CAMP.radius, { trampling: CAMP.trampling, visitation: CAMP.visitation }, at);
  }
  if (impact.travelled) {
    // no EnvField stamp here: travel wear becomes visible through paths.ts,
    // which stamps the actual walked line, not a disc at the day's end
    pressure.stamp(x, z, TRAVEL.radius, { trampling: TRAVEL.trampling, visitation: TRAVEL.visitation }, at);
  }
  return [...dirty];
}
